// ═══════════════════════════════════════════════════════════════
// GENERADOR DE PLANILLA TSS — Motor Configurable
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import {
  Search, Download, AlertCircle, CheckCircle,
  AlertTriangle, ChevronDown, ChevronUp, Loader2, RefreshCw,
  FileText, Settings, Save,
} from 'lucide-react';
import { getTSSPeriodos, getTSSPreview, generarPlanillaTSS, saveTSSOverrides } from '../../../../api/tss';

const fmt = (v) =>
  'RD$ ' + parseFloat(v || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtM = (v) =>
  parseFloat(v || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ── Definición de tipos de planilla ──────────────────────────
const TIPOS = [
  { id: 'AM', label: 'AM',  desc: 'Autodeterminación Mensual',  color: 'bg-blue-600'   },
  { id: 'RT', label: 'RT',  desc: 'Rectificativa',              color: 'bg-amber-600'  },
  { id: 'BN', label: 'BN',  desc: 'Bonificación INFOTEP',       color: 'bg-green-600'  },
  { id: 'DA', label: 'DA',  desc: 'Dependientes Adicionales',   color: 'bg-purple-600' },
];

// ── Columnas por tipo para la tabla de preview ────────────────
const COLUMNAS = {
  AM: [
    { key: 'clave_nomina',                label: 'Clave Nómina',     align: 'left',   editable: true },
    { key: 'tipo_doc',                    label: 'Tipo Doc.',        align: 'center', editable: true },
    { key: 'numero_doc',                  label: 'Cédula',           align: 'left'  },
    { key: 'nombres',                     label: 'Nombres',          align: 'left'  },
    { key: 'primer_apellido',             label: '1er Apellido',     align: 'left'  },
    { key: 'segundo_apellido',            label: '2do Apellido',     align: 'left'  },
    { key: 'sexo',                        label: 'Sexo',             align: 'center'},
    { key: 'fecha_nacimiento',            label: 'F. Nac.',          align: 'center', tipo: 'fecha' },
    { key: 'salario_cotizable',           label: 'Sal. Cotizable',   align: 'right',  tipo: 'monto' },
    { key: 'aporte_voluntario',           label: 'Ap. Vol.',         align: 'right',  tipo: 'monto' },
    { key: 'salario_isr',                 label: 'Sal. ISR',         align: 'right',  tipo: 'monto' },
    { key: 'tipo_ingreso',                label: 'Tipo Ing.',        align: 'center', editable: true },
    { key: 'otras_remuneraciones',        label: 'Otras Rem.',       align: 'right',  tipo: 'monto' },
    { key: 'rnc_agente_ret',             label: 'RNC Ag. Ret.',     align: 'left'  },
    { key: 'remuneracion_otros_agentes',  label: 'Rem. Otros Ag.',   align: 'right',  tipo: 'monto' },
    { key: 'saldo_a_favor',              label: 'Saldo Favor',      align: 'right',  tipo: 'monto' },
    { key: 'regalia_pascual',            label: 'Regalía P.',       align: 'right',  tipo: 'monto' },
    { key: 'preaviso_cesantia',          label: 'Preaviso/Ces.',    align: 'right',  tipo: 'monto' },
    { key: 'retencion_pension_alimenticia', label: 'Pensión Al.',   align: 'right',  tipo: 'monto' },
    { key: 'salario_infotep',            label: 'Sal. INFOTEP',     align: 'right',  tipo: 'monto' },
  ],
  RT: [
    { key: 'clave_nomina',               label: 'Clave Nómina',     align: 'left',   editable: true },
    { key: 'tipo_doc',                   label: 'Tipo Doc.',        align: 'center', editable: true },
    { key: 'numero_doc',                 label: 'Cédula',           align: 'left'  },
    { key: 'nombres',                    label: 'Nombres',          align: 'left'  },
    { key: 'primer_apellido',            label: '1er Apellido',     align: 'left'  },
    { key: 'segundo_apellido',           label: '2do Apellido',     align: 'left'  },
    { key: 'sexo',                       label: 'Sexo',             align: 'center'},
    { key: 'fecha_nacimiento',           label: 'F. Nac.',          align: 'center', tipo: 'fecha' },
    { key: 'salario_cotizable',          label: 'Sal. Cotizable',   align: 'right',  tipo: 'monto' },
    { key: 'aporte_voluntario',          label: 'Ap. Vol.',         align: 'right',  tipo: 'monto' },
    { key: 'salario_isr',               label: 'Sal. ISR',         align: 'right',  tipo: 'monto' },
    { key: 'otras_remuneraciones',       label: 'Otras Rem.',       align: 'right',  tipo: 'monto' },
    { key: 'saldo_a_favor',             label: 'Saldo Favor',      align: 'right',  tipo: 'monto' },
    { key: 'regalia_pascual',           label: 'Regalía P.',       align: 'right',  tipo: 'monto' },
    { key: 'preaviso_cesantia',         label: 'Preaviso/Ces.',    align: 'right',  tipo: 'monto' },
    { key: 'retencion_pension_alimenticia', label: 'Pensión Al.',  align: 'right',  tipo: 'monto' },
  ],
  BN: [
    { key: 'tipo_doc',                   label: 'Tipo Doc.',        align: 'center', editable: true },
    { key: 'numero_doc',                 label: 'Cédula',           align: 'left'  },
    { key: 'nombres',                    label: 'Nombres',          align: 'left'  },
    { key: 'primer_apellido',           label: '1er Apellido',     align: 'left'  },
    { key: 'segundo_apellido',          label: '2do Apellido',     align: 'left'  },
    { key: 'sexo',                      label: 'Sexo',             align: 'center'},
    { key: 'fecha_nacimiento',          label: 'F. Nac.',          align: 'center', tipo: 'fecha' },
    { key: 'monto_bonificacion',        label: 'Monto Bonif.',     align: 'right',  tipo: 'monto' },
  ],
  DA: [
    { key: 'clave_nomina_titular',  label: 'Nómina',           align: 'left'  },
    { key: 'tipo_doc_titular',      label: 'Tipo Doc. Titular', align: 'center', editable: true, editKey: 'tipo_doc',     editFor: 'employee' },
    { key: 'nro_doc_titular',       label: 'Nro. Doc. Titular', align: 'left'  },
    { key: 'tipo_novedad',          label: 'Tipo Novedad',      align: 'center' },
    { key: 'tipo_doc_dep',          label: 'Tipo Doc. Dep.',    align: 'center', editable: true, editKey: 'tipo_doc_dep', editFor: 'dependent' },
    { key: 'nro_doc_dep',           label: 'Nro. Doc. Dep.',    align: 'left'  },
    { key: 'nombres_dep',           label: 'Nombres Dep.',      align: 'left'  },
    { key: 'primer_apellido_dep',   label: '1er Ap. Dep.',      align: 'left'  },
    { key: 'segundo_apellido_dep',  label: '2do Ap. Dep.',      align: 'left'  },
  ],
};

function fmtFechaDisplay(d) {
  if (!d) return '—';
  const dt = new Date(d);
  return `${String(dt.getUTCDate()).padStart(2,'0')}/${String(dt.getUTCMonth()+1).padStart(2,'0')}/${dt.getUTCFullYear()}`;
}

function CeldaValor({ col, row }) {
  const val = row[col.key];
  if (col.tipo === 'monto') {
    return <span className="tabular-nums">{fmtM(val)}</span>;
  }
  if (col.tipo === 'fecha') {
    return <span>{fmtFechaDisplay(val)}</span>;
  }
  if (col.key === 'tipo_novedad') {
    return (
      <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${val === 'ID' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
        {val === 'ID' ? 'ID Inclusión' : 'SD Exclusión'}
      </span>
    );
  }
  return <span>{val || '—'}</span>;
}

export default function PlanillaGenerator({ companyId }) {
  const [periodos,        setPeriodos]        = useState([]);
  const [periodo,         setPeriodo]         = useState('');
  const [tipoArchivo,     setTipoArchivo]     = useState('AM');
  const [preview,         setPreview]         = useState(null);
  const [loadingPeriodos, setLoadingPeriodos] = useState(false);
  const [loadingPreview,  setLoadingPreview]  = useState(false);
  const [generating,      setGenerating]      = useState(false);
  const [error,           setError]           = useState(null);
  const [showValidation,  setShowValidation]  = useState(true);
  const [showRegistros,   setShowRegistros]   = useState(true);
  // Overrides editables: { [employee_id]: { tipo_doc, clave_nomina, tipo_ingreso } }
  const [overrides,       setOverrides]       = useState({});
  // Overrides de dependientes: { [dependent_id]: { tipo_doc_dep } }
  const [depOverrides,    setDepOverrides]    = useState({});
  const [savingOverrides, setSavingOverrides] = useState(false);
  const [savedOverrides,  setSavedOverrides]  = useState(false);

  useEffect(() => {
    setLoadingPeriodos(true);
    getTSSPeriodos(companyId)
      .then(res => setPeriodos(res.data || []))
      .catch(() => setError('Error al cargar períodos'))
      .finally(() => setLoadingPeriodos(false));
  }, [companyId]);

  // Reset preview y overrides cuando cambia tipo o periodo
  useEffect(() => {
    setPreview(null);
    setError(null);
    setOverrides({});
    setDepOverrides({});
    setSavedOverrides(false);
  }, [tipoArchivo, periodo]);

  const handleCargarPreview = async () => {
    if (!periodo) return;
    setLoadingPreview(true);
    setPreview(null);
    setError(null);
    try {
      const data = await getTSSPreview(companyId, periodo, tipoArchivo);
      setPreview(data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleGenerar = async (forzar = false) => {
    setGenerating(true);
    setError(null);
    try {
      await generarPlanillaTSS(companyId, periodo, forzar, tipoArchivo);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleOverrideChange = (employeeId, field, value) => {
    setOverrides(prev => ({
      ...prev,
      [employeeId]: { ...prev[employeeId], [field]: value },
    }));
    setSavedOverrides(false);
  };

  // Valor mostrado: override local → valor del preview → default por campo
  const FIELD_DEFAULTS = { tipo_doc: 'C', clave_nomina: '001', tipo_ingreso: 'Normal' };
  const getVal = (emp, field) => {
    const ov = overrides[emp.employee_id];
    if (ov && ov[field] !== undefined) return ov[field];
    return emp[field] ?? FIELD_DEFAULTS[field] ?? '';
  };

  const handleSaveOverrides = async () => {
    setSavingOverrides(true);
    setError(null);
    try {
      if (tipoArchivo === 'DA') {
        // Guardar tipo_doc del titular (employee) y tipo_doc del dependiente
        // Deduplicar por employeeId (un empleado puede tener múltiples dependientes)
        const seenEmp = new Set();
        const empOvs = preview.employees.reduce((acc, row) => {
          if (!seenEmp.has(row.employee_id)) {
            seenEmp.add(row.employee_id);
            acc.push({
              employeeId:   row.employee_id,
              tipo_doc:     overrides[row.employee_id]?.tipo_doc ?? row.tipo_doc_titular ?? 'C',
              clave_nomina: overrides[row.employee_id]?.clave_nomina ?? row.clave_nomina_titular ?? '001',
              tipo_ingreso: 'Normal',
            });
          }
          return acc;
        }, []);
        const depOvs = preview.employees.map(row => ({
          dependentId: row.dependent_id,
          tipo_doc:    depOverrides[row.dependent_id]?.tipo_doc_dep ?? row.tipo_doc_dep ?? 'C',
        }));
        await saveTSSOverrides(companyId, empOvs, depOvs);
      } else {
        const toSave = preview.employees.map(emp => ({
          employeeId:   emp.employee_id,
          tipo_doc:     getVal(emp, 'tipo_doc'),
          clave_nomina: getVal(emp, 'clave_nomina'),
          tipo_ingreso: getVal(emp, 'tipo_ingreso'),
        }));
        await saveTSSOverrides(companyId, toSave);
      }
      setSavedOverrides(true);
      setTimeout(() => setSavedOverrides(false), 3000);
      const data = await getTSSPreview(companyId, periodo, tipoArchivo);
      setPreview(data);
      setOverrides({});
      setDepOverrides({});
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSavingOverrides(false);
    }
  };

  const hasUnsavedOverrides = Object.keys(overrides).length > 0 || Object.keys(depOverrides).length > 0;

  const tipoInfo     = TIPOS.find(t => t.id === tipoArchivo) || TIPOS[0];
  const tieneErrores = preview?.validation?.errores?.length > 0;
  const tieneAdvert  = preview?.validation?.advertencias?.length > 0;
  const columnas     = COLUMNAS[tipoArchivo] || COLUMNAS.AM;
  // DA usa selector de mes libre (no restringido a nóminas cerradas)
  const isDATipo     = tipoArchivo === 'DA';

  return (
    <div className="space-y-4">

      {/* ── Selector de tipo ── */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Tipo de planilla</h3>
        <div className="flex flex-wrap gap-2">
          {TIPOS.map(t => (
            <button
              key={t.id}
              onClick={() => setTipoArchivo(t.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 text-sm font-semibold transition-all ${
                tipoArchivo === t.id
                  ? `${t.color} text-white border-transparent shadow-md`
                  : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
              }`}
            >
              <FileText size={14} />
              <span>{t.id}</span>
              <span className={`text-xs font-normal hidden sm:inline ${tipoArchivo === t.id ? 'text-white/80' : 'text-gray-400'}`}>
                {t.desc}
              </span>
            </button>
          ))}
        </div>
        <p className="mt-2 text-xs text-gray-400">{tipoInfo.desc}</p>
      </div>

      {/* ── Selector de período ── */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Seleccionar período</h3>
        <div className="flex gap-3 flex-wrap items-end">

          {isDATipo ? (
            /* DA: selector de mes libre — detecta novedades registradas ese mes */
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500">Mes de reporte</label>
              <input
                type="month"
                value={periodo}
                onChange={e => { setPeriodo(e.target.value); setPreview(null); }}
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-purple-400 min-w-[180px]"
              />
              <p className="text-xs text-gray-400">
                Muestra altas (ID) y bajas (SD) de dependientes registradas en ese mes
              </p>
            </div>
          ) : (
            /* AM / RT / BN: períodos con nóminas cerradas */
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500">Período</label>
              <select
                value={periodo}
                onChange={e => { setPeriodo(e.target.value); setPreview(null); }}
                disabled={loadingPeriodos}
                className="appearance-none px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-blue-400 min-w-[220px]"
              >
                <option value="">
                  {loadingPeriodos ? 'Cargando...' : 'Selecciona un período'}
                </option>
                {periodos.map(p => (
                  <option key={p.periodo} value={p.periodo}>
                    {p.periodo} — {p.nominas_cerradas} nómina(s) cerrada(s)
                  </option>
                ))}
              </select>
            </div>
          )}

          <button
            onClick={handleCargarPreview}
            disabled={!periodo || loadingPreview}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {loadingPreview
              ? <><Loader2 size={14} className="animate-spin" /> Cargando...</>
              : <><Search size={14} /> Ver Planilla</>
            }
          </button>
        </div>
      </div>

      {/* ── Error global ── */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
          <AlertCircle size={15} className="flex-shrink-0" /> {error}
        </div>
      )}

      {/* ── Preview ── */}
      {preview && (
        <div className="space-y-4">

          {/* Totales */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {tipoArchivo === 'DA' && (() => {
              const altas = preview.employees.filter(e => e.tipo_novedad === 'ID').length;
              const bajas = preview.employees.filter(e => e.tipo_novedad === 'SD').length;
              return (
                <>
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <p className="text-xs text-gray-500 mb-1">Total novedades</p>
                    <p className="text-lg font-bold text-gray-900">{preview.totales.empleados}</p>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-xs text-green-600 mb-1">Inclusiones (ID)</p>
                    <p className="text-lg font-bold text-green-800">{altas}</p>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-xs text-red-600 mb-1">Exclusiones (SD)</p>
                    <p className="text-lg font-bold text-red-800">{bajas}</p>
                  </div>
                  {preview.totales.empleados === 0 && (
                    <div className="col-span-4 text-center py-2 text-sm text-gray-400">
                      Sin novedades de dependientes en este período
                    </div>
                  )}
                </>
              );
            })()}
            {tipoArchivo === 'BN' && <>
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">Empleados</p>
                <p className="text-lg font-bold text-gray-900">{preview.totales.empleados}</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-4 col-span-3">
                <p className="text-xs text-gray-500 mb-1">Total Bonificaciones</p>
                <p className="text-lg font-bold text-gray-900">{fmt(preview.totales.monto_bonificacion)}</p>
              </div>
            </>}
            {(tipoArchivo === 'AM' || tipoArchivo === 'RT') && [
              { label: 'Empleados',         value: preview.totales.empleados,         money: false },
              { label: 'Salario Cotizable', value: preview.totales.salario_cotizable, money: true  },
              { label: 'Salario ISR',       value: preview.totales.salario_isr,       money: true  },
              ...(tipoArchivo === 'AM' ? [{ label: 'Base INFOTEP', value: preview.totales.salario_infotep, money: true }] : []),
            ].map(card => (
              <div key={card.label} className="bg-white border border-gray-200 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">{card.label}</p>
                <p className="text-lg font-bold text-gray-900">
                  {card.money ? fmt(card.value) : card.value}
                </p>
              </div>
            ))}
          </div>

          {/* Versión de formato */}
          {preview.formatConfig && (
            <div className="flex items-center gap-2 text-xs text-gray-400 px-1">
              <Settings size={11} />
              Formato {preview.tipoArchivo} v{preview.formatConfig.version} — {preview.formatConfig.descripcion}
            </div>
          )}

          {/* Panel de validación */}
          {(tieneErrores || tieneAdvert) && (
            <div className={`border rounded-xl overflow-hidden ${tieneErrores ? 'border-red-300' : 'border-amber-300'}`}>
              <button
                onClick={() => setShowValidation(v => !v)}
                className={`w-full flex items-center justify-between px-4 py-3 text-sm font-semibold ${
                  tieneErrores ? 'bg-red-50 text-red-800' : 'bg-amber-50 text-amber-800'
                }`}
              >
                <div className="flex items-center gap-2">
                  {tieneErrores ? <AlertCircle size={15} /> : <AlertTriangle size={15} />}
                  {tieneErrores
                    ? `${preview.validation.errores.length} error(es) que bloquean la exportación`
                    : `${preview.validation.advertencias.length} advertencia(s) — se puede exportar`
                  }
                </div>
                {showValidation ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
              {showValidation && (
                <div className="divide-y divide-gray-100 max-h-56 overflow-y-auto">
                  {[...preview.validation.errores, ...preview.validation.advertencias].map((v, i) => (
                    <div key={i} className={`flex items-start gap-3 px-4 py-2.5 text-xs ${
                      v.tipo === 'error' ? 'bg-red-50/60' : 'bg-amber-50/40'
                    }`}>
                      {v.tipo === 'error'
                        ? <AlertCircle size={13} className="text-red-500 flex-shrink-0 mt-0.5" />
                        : <AlertTriangle size={13} className="text-amber-500 flex-shrink-0 mt-0.5" />
                      }
                      <div>
                        <span className="font-medium text-gray-800">{v.empleado}</span>
                        <span className="text-gray-500"> · {v.campo}: </span>
                        <span className={v.tipo === 'error' ? 'text-red-700' : 'text-amber-700'}>{v.detalle}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {!tieneErrores && !tieneAdvert && (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700">
              <CheckCircle size={15} /> Todos los datos son válidos. Listo para exportar.
            </div>
          )}

          {/* Tabla de registros */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <button
              onClick={() => setShowRegistros(v => !v)}
              className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-gray-700 bg-gray-50 hover:bg-gray-100"
            >
              <span>
                {tipoArchivo === 'DA' ? 'Dependientes incluidos' : 'Empleados incluidos'} ({preview.employees.length})
              </span>
              {showRegistros ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>

            {showRegistros && (
              <div className="overflow-x-auto max-h-96 overflow-y-auto">
                <table className="min-w-full text-xs">
                  <thead className="sticky top-0">
                    <tr className="bg-blue-800 text-white">
                      {columnas.map(col => (
                        <th
                          key={col.key}
                          className={`px-3 py-2.5 font-semibold whitespace-nowrap text-${col.align}`}
                        >
                          {col.label}
                        </th>
                      ))}
                      {(tipoArchivo === 'AM' || tipoArchivo === 'RT') && (
                        <th className="px-3 py-2.5 text-center font-semibold">OK</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.employees.map((row, idx) => {
                      const cedula = (row.numero_doc || '').replace(/\D/g, '');
                      const ok = tipoArchivo === 'BN' || tipoArchivo === 'DA'
                        ? true
                        : cedula.length === 11 && parseFloat(row.salario_cotizable || 0) > 0;
                      const isAMRT = tipoArchivo === 'AM' || tipoArchivo === 'RT';
                      return (
                        <tr
                          key={idx}
                          className={`border-b border-gray-100 ${
                            idx % 2 === 1 ? 'bg-gray-50' : 'bg-white'
                          } ${!ok ? 'bg-red-50' : ''}`}
                        >
                          {columnas.map(col => {
                            if (col.editable) {
                              // tipo_doc del dependiente (DA)
                              if (col.editFor === 'dependent') return (
                                <td key={col.key} className="px-2 py-1.5 text-center">
                                  <select
                                    value={depOverrides[row.dependent_id]?.tipo_doc_dep ?? row.tipo_doc_dep ?? 'C'}
                                    onChange={e => {
                                      setDepOverrides(prev => ({
                                        ...prev,
                                        [row.dependent_id]: { ...prev[row.dependent_id], tipo_doc_dep: e.target.value },
                                      }));
                                      setSavedOverrides(false);
                                    }}
                                    className="text-xs border border-gray-200 rounded px-1 py-0.5 bg-white focus:outline-none focus:border-blue-400 w-14"
                                  >
                                    <option value="C">C</option>
                                    <option value="N">N</option>
                                  </select>
                                </td>
                              );
                              // tipo_doc del titular/empleado — en DA solo C/N, en otros C/N/P
                              if (col.key === 'tipo_doc' || col.key === 'tipo_doc_titular') return (
                                <td key={col.key} className="px-2 py-1.5 text-center">
                                  <select
                                    value={getVal(row, 'tipo_doc')}
                                    onChange={e => handleOverrideChange(row.employee_id, 'tipo_doc', e.target.value)}
                                    className="text-xs border border-gray-200 rounded px-1 py-0.5 bg-white focus:outline-none focus:border-blue-400 w-14"
                                  >
                                    <option value="C">C</option>
                                    <option value="N">N</option>
                                    {tipoArchivo !== 'DA' && <option value="P">P</option>}
                                  </select>
                                </td>
                              );
                              if (col.key === 'clave_nomina') return (
                                <td key={col.key} className="px-2 py-1.5 text-left">
                                  <input
                                    type="number"
                                    min={1}
                                    step={1}
                                    value={getVal(row, 'clave_nomina')}
                                    onChange={e => {
                                      const v = e.target.value.replace(/\D/g, '');
                                      if (v === '' || parseInt(v, 10) >= 1)
                                        handleOverrideChange(row.employee_id, 'clave_nomina', v === '' ? '001' : v);
                                    }}
                                    className="text-xs border border-gray-200 rounded px-2 py-0.5 w-20 font-mono focus:outline-none focus:border-blue-400"
                                  />
                                </td>
                              );
                              if (col.key === 'tipo_ingreso') return (
                                <td key={col.key} className="px-2 py-1.5 text-center">
                                  <select
                                    value={getVal(row, 'tipo_ingreso')}
                                    onChange={e => handleOverrideChange(row.employee_id, 'tipo_ingreso', e.target.value)}
                                    className="text-xs border border-gray-200 rounded px-1 py-0.5 bg-white focus:outline-none focus:border-blue-400 max-w-[180px]"
                                  >
                                    <option value="Normal">Normal</option>
                                    <option value="Trabajador ocasional (no fijo)">Trabajador ocasional (no fijo)</option>
                                    <option value="Asalariado por hora o labora tiempo parcial">Asalariado por hora o labora tiempo parcial</option>
                                    <option value="No laboró mes completo por razones varias">No laboró mes completo por razones varias</option>
                                    <option value="Salario prorrateado semanal/bisemanal">Salario prorrateado semanal/bisemanal</option>
                                    <option value="Pensionado antes de la Ley 87-01">Pensionado antes de la Ley 87-01</option>
                                    <option value="Exento por Ley de pago al SDSS">Exento por Ley de pago al SDSS</option>
                                    <option value="Trabajador con salario sectorizado">Trabajador con salario sectorizado</option>
                                  </select>
                                </td>
                              );
                            }
                            return (
                              <td
                                key={col.key}
                                className={`px-3 py-2 text-${col.align} text-gray-700 whitespace-nowrap`}
                              >
                                <CeldaValor col={col} row={row} />
                              </td>
                            );
                          })}
                          {isAMRT && (
                            <td className="px-3 py-2 text-center">
                              {ok
                                ? <CheckCircle size={13} className="text-green-500 mx-auto" />
                                : <AlertCircle size={13} className="text-red-500 mx-auto" />
                              }
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Botones exportar */}
          <div className="flex items-center gap-3 justify-end flex-wrap">
            {/* Guardar cambios de tipo_doc / clave_nomina / tipo_ingreso */}
            <button
              onClick={handleSaveOverrides}
              disabled={savingOverrides || (!hasUnsavedOverrides && !savedOverrides)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg border transition-colors disabled:opacity-50 ${
                savedOverrides
                  ? 'border-green-300 text-green-700 bg-green-50'
                  : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
              }`}
            >
              {savingOverrides
                ? <><Loader2 size={14} className="animate-spin" /> Guardando...</>
                : savedOverrides
                  ? <><CheckCircle size={14} /> Cambios guardados</>
                  : <><Save size={14} /> Guardar cambios</>
              }
            </button>

            {tieneErrores && (
              <button
                onClick={() => handleGenerar(true)}
                disabled={generating}
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-orange-700 border border-orange-300 bg-orange-50 rounded-lg hover:bg-orange-100 disabled:opacity-50 transition-colors"
              >
                {generating ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                Exportar de todas formas
              </button>
            )}
            <button
              onClick={() => handleGenerar(false)}
              disabled={generating || tieneErrores || preview.employees.length === 0}
              className={`flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-white rounded-lg disabled:opacity-50 transition-colors shadow ${tipoInfo.color} hover:opacity-90`}
            >
              {generating
                ? <><Loader2 size={14} className="animate-spin" /> Generando...</>
                : <><Download size={14} /> Exportar TXT {tipoArchivo}</>
              }
            </button>
          </div>

        </div>
      )}
    </div>
  );
}
